import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';

// Custom hook for scroll float effect
const useScrollFloat = (ref, speed = 0.5) => {
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  
  return { y: useTransform(y, (value) => value * speed), opacity };
};

const AboutSection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #ffd93d 0%, #ff6b6b 50%, #6bcf7f 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
    min-height: auto;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  @media (max-width: 480px) {
    gap: 1.5rem;
  }
`;

const Content = styled.div`
  color: white;
  
  @media (max-width: 768px) {
    order: 2;
  }
`;

const Title = styled(motion.h2)`
  font-size: 2rem;
  margin-bottom: 2rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1.2rem;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 1.2rem;
  }
`;

const Features = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.8rem;
    margin-top: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.6rem;
    margin-top: 1.2rem;
  }
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    gap: 0.4rem;
  }
  
  span {
    font-size: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.2rem;
    }
  }
`;

const ImageContainer = styled(motion.div)`
  text-align: center;
  
  @media (max-width: 768px) {
    order: 1;
  }
  
  img {
    max-width: 100%;
    height: auto;
    filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3));
    
    @media (max-width: 768px) {
      max-width: 80%;
    }
    
    @media (max-width: 480px) {
      max-width: 70%;
    }
  }
`;

const CryptoTeaser = styled(motion.div)`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 2rem;
  margin-top: 2rem;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-top: 1.5rem;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 1.2rem;
    margin-top: 1.2rem;
    border-radius: 12px;
  }
`;

const CryptoTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 0.6rem;
  }
`;

const CryptoText = styled.p`
  color: white;
  font-size: 1rem;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const About = () => {
  const titleRef = useRef(null);
  const desc1Ref = useRef(null);
  const desc2Ref = useRef(null);
  const featuresRef = useRef(null);
  
  const titleFloat = useScrollFloat(titleRef, 0.3);
  const desc1Float = useScrollFloat(desc1Ref, 0.4);
  const desc2Float = useScrollFloat(desc2Ref, 0.5);
  const featuresFloat = useScrollFloat(featuresRef, 0.6);

  return (
    <AboutSection id="about">
      <Container>
        <Content>
          <motion.div
            ref={titleRef}
            style={{ y: titleFloat.y, opacity: titleFloat.opacity }}
          >
            <Title>
              The Story of Chikin Lady 🐔
            </Title>
          </motion.div>
          
          <motion.div
            ref={desc1Ref}
            style={{ y: desc1Float.y, opacity: desc1Float.opacity }}
          >
            <Description>
              Born from a love of all things chicken and a passion for fun, 
              Chikin Lady represents the wild, free spirit in all of us. 
              Our designs celebrate the joy, humor, and attitude that makes 
              life worth living!
            </Description>
          </motion.div>
          
          <motion.div
            ref={desc2Ref}
            style={{ y: desc2Float.y, opacity: desc2Float.opacity }}
          >
            <Description>
              From bandanas to bikinis, Christmas cheer to badass attitude, 
              we've got a chicken for every mood and occasion. Each design 
              is crafted with love and a whole lot of cluckin' personality!
            </Description>
          </motion.div>
          
          <motion.div
            ref={featuresRef}
            style={{ y: featuresFloat.y, opacity: featuresFloat.opacity }}
          >
            <Features>
              <Feature>
                <span>🐔</span>
                <span>Henpecked for you</span>
              </Feature>
              <Feature>
                <span>🎨</span>
                <span>High Quality</span>
              </Feature>
              <Feature>
                <span>💪</span>
                <span>Attitude</span>
              </Feature>
              <Feature>
                <span>🌟</span>
                <span>Personality</span>
              </Feature>
            </Features>
          </motion.div>
          
          <CryptoTeaser
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.02 }}
          >
            <CryptoTitle>🚀 Coming Soon: Chikin Lady App!</CryptoTitle>
            <CryptoText>
              Get ready for the most cluckin' app ever! 
              We're working on something egg-citing that will revolutionize 
              how you interact with the Chikin Lady brand. Stay tuned for 
              updates on our digital adventure!
            </CryptoText>
          </CryptoTeaser>
        </Content>
        
        <ImageContainer
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <motion.img
            src="/assets/chikinladyLOGO-2.png"
            alt="Chikin Lady Story"
            whileHover={{ 
              scale: 1.1,
              rotate: [0, -5, 5, 0]
            }}
            transition={{ duration: 0.5 }}
          />
        </ImageContainer>
      </Container>
    </AboutSection>
  );
};

export default About;
